import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { compressImageWithRetry } from "../field_image_compressor"
import { getLogContext, getPlatformSnapshot, logEvent, logError } from "../field_logger"
import { queueCard, syncCards, cardsForDisplay, removeCard, photoDroppedNote, CARDS_CHANGED_EVENT } from "../field_card_sync"
import { showFieldFlash } from "../lib/field_flash"

// Raise a Ticket page (card #2617) -- /field/cards.
//
// Submit ALWAYS writes the card to Dexie first (queueCard), then tries an
// immediate send when the device is online. Offline, the card waits in the
// queue and field_sync_controller.js flushes it on the next sync cycle; this
// page also flushes on field:online so the tasker sees it go through.
//
// Context (place/mission/response) comes from the query string the profile
// dropdown link adds, falling back to app_logger's in-memory context -- the
// page can be served from the offline shell with no server-side knowledge of
// where the tasker came from.
//
// Queued rows are cloned from the <template> in the view -- no HTML in JS.
//
// The "Who's it for?" select's options are server-rendered into the cached
// shell (Hub.for_field_cards). Each carries data-hub-label ("General IT
// Hub"), which hubChanged copies into the intro line's hubName target.
//
// Outcomes are field flashes (lib/field_flash.js): Sent, Saved on this phone,
// or Not sent with the reason. The lasting state is on the "On this phone"
// rows (Queued / Not sent badges); sent cards move to My tickets.
//
// Categories are the Hubs' own category tags ("it-support:sync"): one
// server-rendered slim <select multiple> per Hub (categoryGroup, data-hub-id,
// categorySelect). Only the selected Hub's shows; switching Hub clears the
// others so a category picked under one Hub can't ride along. None picked is
// fine -- the card files as Other.
//
// Photos (card #4190): the tasker can attach several, up to maxPhotos (the
// server's Requests::CreateFromField::MAX_PHOTOS, passed in by the view). Each
// chosen photo is listed from the <template> in the view with its own Remove.
// What the tasker is told when a photo does not go is built in
// field_card_sync.js (photoDroppedNote).
//
// Usage: see app/views/field/cards/index.html.erb
export default class extends Controller {
  static targets = [
    "form", "hub", "hubName", "categorySection", "categoryGroup", "categorySelect", "body", "photoInput", "photoPicker", "photoPickerFirst", "photoPickerMore", "photoList", "photoRowTemplate",
    "error", "submitButton", "shareButton",
    "queueSection", "queueList", "queueRowTemplate", "myCards", "myCardsOffline"
  ]

  static values = {
    appVersion: { type: String, default: "" },
    maxPhotos: { type: Number, default: 5 },
    userName: { type: String, default: "" },
    // Set by the response page's "Raise a Card" bottom sheet (#4517), which
    // has no query string of its own to carry place/mission/response_id the
    // way the full /field/cards page does. Take precedence over the query
    // string and the ambient log context when present.
    placeId: { type: String, default: "" },
    missionId: { type: String, default: "" },
    responseId: { type: String, default: "" }
  }

  connect() {
    this._photos = []
    this._submitting = false
    this._lastQueuedId = null

    if (this.hasShareButtonTarget && typeof navigator.share === "function") {
      this.shareButtonTarget.classList.remove("hidden")
    }

    this._onChanged = () => {
      this._renderQueue()
      this._announceOutcome()
    }
    window.addEventListener(CARDS_CHANGED_EVENT, this._onChanged)

    this._onOnline = () => this._flush()
    window.addEventListener("field:online", this._onOnline)

    this._renderQueue()
    this._flush()
  }

  disconnect() {
    window.removeEventListener(CARDS_CHANGED_EVENT, this._onChanged)
    window.removeEventListener("field:online", this._onOnline)
  }

  hubChanged() {
    const label = this._selectedHubLabel()
    if (label) this.hubNameTargets.forEach(target => { target.textContent = label })

    const hubId = this.hasHubTarget ? this.hubTarget.value : null
    let hasCategories = false

    this.categoryGroupTargets.forEach(group => {
      const selected = group.dataset.hubId === hubId
      group.classList.toggle("hidden", !selected)
      const select = group.querySelector("select")
      if (selected) hasCategories = (select?.options.length || 0) > 0
      if (!selected && select) this._clearSelect(select)
    })

    if (this.hasCategorySectionTarget) this.categorySectionTarget.classList.toggle("hidden", !hasCategories)
  }


  photoChosen() {
    const chosen = Array.from(this.photoInputTarget.files || [])
    // Clear the input so the same photo can be picked again after a Remove.
    this.photoInputTarget.value = ""
    if (chosen.length === 0) return

    this._hideError()
    const room = Math.max(this.maxPhotosValue - this._photos.length, 0)
    this._photos.push(...chosen.slice(0, room))

    if (chosen.length > room) {
      this._showError(`You can attach up to ${this.maxPhotosValue} photos.`)
    }
    this._renderPhotos()
  }

  removePhoto(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(index)) return

    this._photos.splice(index, 1)
    this._hideError()
    this._renderPhotos()
  }

  clearPhotos() {
    this._photos = []
    if (this.hasPhotoInputTarget) this.photoInputTarget.value = ""
    this._renderPhotos()
  }

  async submit(event) {
    event.preventDefault()
    if (this._submitting) return

    const card = this._readForm()
    if (!card) return

    this._submitting = true
    this.submitButtonTarget.disabled = true

    try {
      const photos = await this._preparePhotos()
      if (photos === false) return

      const record = await queueCard({ ...card, photos })
      this._lastQueuedId = record.id
      this._lastHubPhrase = this._hubPhrase()
      this._resetForm()

      await this._flush()
      await this._announceOutcome({ stillQueued: true })
    } catch (error) {
      logError("cards", "Card could not be saved", { message: error?.message }).catch(() => {})
      this._showError("Couldn't save your ticket on this phone. Try again, or use Send by text message.")
    } finally {
      this._submitting = false
      this.submitButtonTarget.disabled = false
    }
  }

  async share() {
    const card = this._readForm()
    if (!card) return

    try {
      await navigator.share({ text: this._composeShareText(card) })
      logEvent("cards", "Card shared by text", { categoryTags: card.categoryTags }).catch(() => {})
    } catch (error) {
      // AbortError is the tasker closing the share sheet -- not a failure.
      if (error?.name !== "AbortError") {
        logError("cards", "Card share failed", { message: error?.message }).catch(() => {})
      }
    }
  }

  async remove(event) {
    const id = Number(event.currentTarget.dataset.cardId)
    if (!id) return

    await removeCard(id)
  }

  myCardsUnavailable(event) {
    // frame-missing's default renders "Content missing" into the frame; the
    // offline state reads better as the page's normal empty state.
    event.preventDefault()
    if (this.hasMyCardsTarget) this.myCardsTarget.classList.add("hidden")
    if (this.hasMyCardsOfflineTarget) this.myCardsOfflineTarget.classList.remove("hidden")
  }

  // -- private --

  _readForm() {
    this._hideError()

    const categoryTags = this._visibleCategorySelect()
      ? Array.from(this._visibleCategorySelect().selectedOptions, option => option.value)
      : []
    const body = this.hasBodyTarget ? this.bodyTarget.value.trim() : ""

    if (!body) {
      logEvent("cards", "Card form rejected", { reason: "empty_body" }).catch(() => {})
      this._showError("Tell us what's going wrong.")
      return null
    }

    const hubId = this.hasHubTarget ? this.hubTarget.value || null : null

    return { hubId, categoryTags, body, context: this._context() }
  }

  // Returns the compressed photos (an empty list when there are none), or
  // false when a chosen photo can't be used (the tasker is told which and
  // nothing is queued).
  async _preparePhotos() {
    const prepared = []

    for (const [index, photo] of this._photos.entries()) {
      try {
        prepared.push(await compressImageWithRetry(photo))
      } catch (error) {
        logError("cards", "Card photo could not be prepared", {
          message: error?.message,
          reason: error?.reason || null,
          size: photo.size
        }).catch(() => {})
        const which = this._photos.length > 1 ? `Photo ${index + 1}` : "That photo"
        this._showError(`${which} couldn't be used. Remove it or pick a different one.`)
        return false
      }
    }

    return prepared
  }

  // Lists each chosen photo from the template, and swaps the picker's wording
  // once there is one. The picker goes away at the cap.
  _renderPhotos() {
    if (!this.hasPhotoListTarget || !this.hasPhotoRowTemplateTarget) return

    const rows = this._photos.map((photo, index) => {
      const node = this.photoRowTemplateTarget.content.firstElementChild.cloneNode(true)
      node.querySelector("[data-slot='name']").textContent = photo.name || "Photo attached"
      node.querySelector("[data-slot='remove']").dataset.index = index
      return node
    })
    this.photoListTarget.replaceChildren(...rows)
    this.photoListTarget.classList.toggle("hidden", rows.length === 0)

    const atCap = this._photos.length >= this.maxPhotosValue
    this.photoPickerTarget.classList.toggle("hidden", atCap)
    this.photoPickerFirstTarget.classList.toggle("hidden", this._photos.length > 0)
    this.photoPickerMoreTarget.classList.toggle("hidden", this._photos.length === 0)
  }

  _context() {
    const params = new URLSearchParams(window.location.search)
    const ambient = getLogContext()
    const platform = getPlatformSnapshot()

    return {
      placeId: this.placeIdValue || params.get("place_id") || ambient.placeId || null,
      missionId: this.missionIdValue || params.get("mission_id") || ambient.missionId || null,
      responseId: this.responseIdValue || params.get("response_id") || ambient.responseId || null,
      platformOs: platform.os,
      displayMode: platform.displayMode,
      appVersion: this.appVersionValue || null
    }
  }

  _composeShareText({ categoryTags, body, context }) {
    const labels = this._labelsFor(categoryTags)
    const heading = labels.length > 0 ? `WIN Field ticket – ${labels.join(", ")}` : "WIN Field ticket"
    const lines = [heading, "", body, ""]

    const hubLabel = this._selectedHubLabel()
    if (hubLabel) lines.push(`For: ${hubLabel}`)
    if (this.userNameValue) lines.push(`From: ${this.userNameValue}`)
    if (context.placeId) lines.push(`Place #${context.placeId}`)
    if (context.missionId) lines.push(`Survey #${context.missionId}`)
    if (context.responseId) lines.push(`Response #${context.responseId}`)
    lines.push(`Device: ${context.platformOs}, ${context.displayMode}`)
    if (context.appVersion) lines.push(`App version: ${context.appVersion}`)

    return lines.join("\n")
  }

  async _flush() {
    if (localStorage.getItem("field:online") === "false") return

    const csrf = document.querySelector("[name='csrf-token']")?.content
    const { sent } = await syncCards(csrf).catch(() => ({ sent: 0 }))
    if (sent > 0) this._reloadMyCards()
    await this._announceOutcome()
  }

  // One flash per outcome for the card the tasker just submitted: Sent (now,
  // or on a later flush from this page or the header's sync cycle), Not sent
  // when the server rejected it, or -- straight after submit only -- Saved on
  // this phone. Several callers race here (submit, the flush, the
  // cards-changed event); whoever reads the row first claims it, so the
  // tasker never gets the same flash twice.
  async _announceOutcome({ stillQueued = false } = {}) {
    const id = this._lastQueuedId
    if (!id) return

    const saved = await db.pending_cards.get(id).catch(() => null)
    if (this._lastQueuedId !== id) return

    const hub = this._lastHubPhrase || this._hubPhrase()

    if (saved?.status === "sent") {
      this._lastQueuedId = null
      if (saved.photoDropped) {
        showFieldFlash(`Sent – ${hub} has your ticket. ${photoDroppedNote(saved, { sent: true })}`, { tone: "warning", duration: 8000 })
      } else {
        showFieldFlash(`Sent – ${hub} has your ticket`, { tone: "success" })
      }
      this._reloadMyCards()
    } else if (saved?.status === "failed") {
      this._lastQueuedId = null
      showFieldFlash(`Not sent – ${saved.error || "the server couldn't accept this card"}`, { tone: "error", duration: 6000 })
    } else if (stillQueued && !this._announcedQueuedId) {
      this._announcedQueuedId = id
      const note = saved?.photoDropped ? ` ${photoDroppedNote(saved, { sent: true })}` : ""
      showFieldFlash(`Saved on this phone – it'll send to ${hub} when you're back online.${note}`, { tone: "info", duration: note ? 8000 : 4000 })
    }
  }

  // "the General IT Hub", or "Mission Control" when no Hub is configured.
  _hubPhrase() {
    const label = this._selectedHubLabel()
    return label ? `the ${label}` : "Mission Control"
  }

  _reloadMyCards() {
    if (!this.hasMyCardsTarget) return

    this.myCardsTarget.classList.remove("hidden")
    if (this.hasMyCardsOfflineTarget) this.myCardsOfflineTarget.classList.add("hidden")
    this.myCardsTarget.reload?.()
  }

  async _renderQueue() {
    if (!this.hasQueueListTarget || !this.hasQueueRowTemplateTarget) return

    const rows = await cardsForDisplay().catch(() => [])

    this.queueListTarget.replaceChildren(...rows.map(row => this._buildRow(row)))
    this.queueSectionTarget.classList.toggle("hidden", rows.length === 0)
  }

  _buildRow(row) {
    const node = this.queueRowTemplateTarget.content.firstElementChild.cloneNode(true)
    const slot = name => node.querySelector(`[data-slot="${name}"]`)
    const labels = this._labelsFor(row.categoryTags)

    node.dataset.cardStatus = row.status
    slot("title").textContent = labels.length > 0 ? `${labels.join(", ")}: ${row.body}` : row.body
    slot("meta").textContent = `Raised ${this._formatTime(row.queuedAt)}`

    const badge = slot(`badge-${row.status}`)
    if (badge) badge.classList.remove("hidden")

    if (row.photoDropped) {
      const note = slot("note")
      note.textContent = photoDroppedNote(row, { sent: row.status === "sent" })
      note.classList.remove("hidden")
    }

    if (row.status === "failed") {
      const error = slot("error")
      error.textContent = row.error || "The server couldn't accept this card."
      error.classList.remove("hidden")
    }

    // Failed tickets and tickets sent without their photo stay listed until
    // the tasker clears them.
    if (row.status === "failed" || row.status === "sent") {
      const remove = slot("remove")
      remove.dataset.cardId = row.id
      remove.textContent = row.status === "sent" ? "Dismiss" : "Remove"
      remove.classList.remove("hidden")
    }

    return node
  }

  // Category labels live once, in the server-rendered <option>s.
  _labelsFor(tagNames) {
    const labels = Object.fromEntries(
      this.categorySelectTargets.flatMap(select => Array.from(select.options, option => [option.value, option.textContent.trim()]))
    )
    return (tagNames || []).map(name => labels[name]).filter(Boolean)
  }

  _visibleCategorySelect() {
    return this.categorySelectTargets.find(select => !select.closest("[data-field-cards-target='categoryGroup']")?.classList.contains("hidden"))
  }

  // Clears through slim when it has initialised, so its pills follow;
  // otherwise straight on the native select.
  _clearSelect(select) {
    const slim = this.application.getControllerForElementAndIdentifier(select, "slim")?.select
    if (slim) {
      slim.setSelected([])
    } else {
      Array.from(select.options).forEach(option => { option.selected = false })
    }
  }

  _selectedHubLabel() {
    if (!this.hasHubTarget) return null
    return this.hubTarget.selectedOptions[0]?.dataset.hubLabel || null
  }

  _formatTime(iso) {
    try {
      return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    } catch {
      return iso
    }
  }

  _resetForm() {
    this.formTarget.reset()
    this.categorySelectTargets.forEach(select => this._clearSelect(select))
    this.clearPhotos()
    this.hubChanged()
  }

  _showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  _hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }
}
